import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Toastify from "../../utils/tostfy";
import { useBackToList } from "../../hooks/useBackToList";
import { endpoint } from "../../utils/endpoints";
import EyeIcon from "../../assets/image/eye-icon.svg";
import LeftAngle from "../../assets/image/left-angle.svg";
import RightAngle from "../../assets/image/right-angle.svg";
import { formatDateTime } from "../../utils/formatDateTime";
import { tripStatusLabel } from "../../utils/tripStatus";
import { formatInr } from "../../utils/formatAmount";
import PageNumberInput from "../../Components/Pagination/PageNumberInput";

const ACCOUNTANT_API =
  process.env.REACT_APP_ACCOUNTANT_API;

const parseBody = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { rawMessage: text };
  }
};

const getErrorMessage = (payload, response) => {
  if (!payload || typeof payload !== "object") {
    return response.statusText || "Something went wrong. Please try again.";
  }
  return (
    payload.errorMessage ||
    payload.message ||
    payload.error ||
    (Array.isArray(payload.errors) ? payload.errors.join(", ") : null) ||
    `Request failed (${response.status})`
  );
};

const dash = (v) => (v == null || v === "" ? "—" : String(v));

const formatFuelEfficiency = (fuelForm) => {
  const raw = fuelForm?.efficiency ?? fuelForm?.efficency;
  if (raw == null || raw === "") return "—";
  const n = Number(raw);
  if (Number.isNaN(n)) return "—";
  return `${n.toFixed(2)} km/l`;
};

const statusCodeToLabel = (code) => tripStatusLabel(code);

const isNonEmptyObject = (o) =>
  o != null && typeof o === "object" && !Array.isArray(o) && Object.keys(o).length > 0;

const hasTripBreakLocation = (value) => {
  if (value == null || value === "") return false;
  if (typeof value === "object" && !Array.isArray(value)) {
    const id = value._id ?? value.id;
    if (id != null && String(id).trim() !== "") return true;
    const name = value.name;
    return name != null && String(name).trim() !== "";
  }
  return String(value).trim() !== "";
};

/** Location row for Trip Break dropdown (listOfAlllocationForTripBreak). */
const mapTripBreakLocationOption = (loc) => {
  const id = loc._id != null ? String(loc._id) : "";
  const name = loc.name != null && loc.name !== "" ? String(loc.name) : "—";
  return { id, label: name, raw: loc };
};

const pickTripBreakLocationList = (payload) => {
  const data = payload?.data;
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    if (Array.isArray(data.location_data)) return data.location_data;
    if (Array.isArray(data.locations)) return data.locations;
    if (Array.isArray(data.list)) return data.list;
  }
  return [];
};

/** Map `listOfExpence` → `trip_expence_data` row to table row. */
const mapTripExpenceListRow = (row, i) => ({
  id: row._id ?? row.id ?? `exp-${i}`,
  amountSpent: formatInr(row.amount ?? row.amount_spent ?? row.expense_amount),
  description: dash(row.description ?? row.remark),
  dateTime: formatDateTime(row.createdAt ?? row.date_time ?? row.created_at ?? row.date),
  receipt:
    row.recept_image ??
    row.receipt_url ??
    row.receipt_image ??
    row.image ??
    "",
  statusLabel: statusCodeToLabel(row.status),
});

const ViewTripDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSubheading } = useOutletContext();
  const [searchParams] = useSearchParams();
  const tripId = searchParams.get("trip_id")?.trim() || "";

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(!!tripId);
  const [expenseRows, setExpenseRows] = useState([]);
  const [expenseListLoading, setExpenseListLoading] = useState(false);
  const [expenseTotal, setExpenseTotal] = useState(0);
  const [expenseTotalPages, setExpenseTotalPages] = useState(1);

  const applyTripDetailPayload = useCallback((data) => {
    setDetail(data && typeof data === "object" ? data : null);
  }, []);

  const fallbackViewTripHref = useMemo(() => {
    const from = searchParams.get("from")?.trim() || "";
    if (from === "trip-break") return "/trip-assignment-management";

    const routeId = searchParams.get("route_id")?.trim() || "";
    if (!routeId) return "/trip-assignment-management";
    return `/trip-assignment-management/view-trip?route_id=${encodeURIComponent(routeId)}`;
  }, [searchParams]);

  const viewTripHref = useBackToList(fallbackViewTripHref);

  const addExpenseHref = useMemo(() => {
    const routeId = searchParams.get("route_id")?.trim() || "";
    const tid = searchParams.get("trip_id")?.trim() || "";
    const from = searchParams.get("from")?.trim() || "";
    const q = new URLSearchParams();
    if (tid) q.set("trip_id", tid);
    if (routeId) q.set("route_id", routeId);
    if (from) q.set("from", from);
    const qs = q.toString();
    return qs
      ? `/trip-assignment-management/view-trip-details/add-expense?${qs}`
      : "/trip-assignment-management/view-trip-details/add-expense";
  }, [searchParams]);

  useEffect(() => {
    setSubheading("View Trip Details");
    return () => {
      setSubheading("");
    };
  }, [setSubheading]);

  const loadTripDetails = useCallback(
    async ({ showLoading = true, isCancelled } = {}) => {
      const cancelled = () => Boolean(isCancelled?.());

      if (!tripId) {
        setDetail(null);
        setExpenseRows([]);
        setLoading(false);
        return;
      }

      const token = localStorage.getItem("sessionToken");
      if (!token) {
        toast.error("Session expired. Please log in again.");
        setDetail(null);
        setExpenseRows([]);
        setLoading(false);
        return;
      }

      if (showLoading) setLoading(true);
      const url = `${ACCOUNTANT_API}${endpoint.viewTripDetails}?trip_id=${encodeURIComponent(tripId)}`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await parseBody(response);
        if (cancelled()) return;

        if (!response.ok || (payload?.code != null && payload.code !== 200 && payload.code !== 201)) {
          toast.error(getErrorMessage(payload, response));
          setDetail(null);
          setExpenseRows([]);
          return;
        }

        applyTripDetailPayload(payload?.data);
      } catch (err) {
        console.error(err);
        if (!cancelled()) {
          toast.error(
            err?.message === "Failed to fetch"
              ? "Network error. Check your connection."
              : "Could not load trip details."
          );
          setDetail(null);
          setExpenseRows([]);
        }
      } finally {
        if (!cancelled()) setLoading(false);
      }
    },
    [tripId, applyTripDetailPayload]
  );

  useEffect(() => {
    let cancelled = false;
    loadTripDetails({ showLoading: true, isCancelled: () => cancelled });
    return () => {
      cancelled = true;
    };
  }, [loadTripDetails]);

  const [tripEnded, setTripEnded] = useState(false);
  useEffect(() => {
    setTripEnded(false);
  }, [tripId]);

  const view = useMemo(() => {
    if (!detail) return null;
    const td = detail.trip_details;
    const company = td?.company_id;
    const primary = detail.driver_details?.primary_driver_details;
    const secondary = detail.driver_details?.secondary_driver_details;
    const advance =
      detail.trip_adavance_details ??
      detail.trip_advance_details ??
      detail.trip_advance_detail ??
      null;
    const debit = detail.debit_note_details;

    const statusRaw = detail.status ?? td?.status;
    const statusCode =
      statusRaw == null || statusRaw === "" ? null : Number(statusRaw);

    const cancelReasonRaw = detail.trip_cancel_reason ?? td?.trip_cancel_reason ?? "";
    const tripCancelReason =
      cancelReasonRaw != null && String(cancelReasonRaw).trim() !== ""
        ? String(cancelReasonRaw).trim()
        : "";

    const awardAmountRaw = Number(detail.award_amount);
    const fuelForm = detail.fuel_form_details ?? td?.fuel_form_details ?? null;

    return {
      statusCode,
      statusLabel: statusCodeToLabel(statusRaw),
      tripCancelReason,
      hasExistingTripBreak: hasTripBreakLocation(td?.trip_break_location_id),
      tripExpenseButton: Boolean(detail.trip_expense_button),
      summary: {
        tripAmount: formatInr(detail.trip_amount),
        awardAmount: formatInr(detail.award_amount),
        isAwardAmountNegative: !Number.isNaN(awardAmountRaw) && awardAmountRaw < 0,
        debitNote: formatInr(detail.debit_note_amount),
        tripAdvance: formatInr(detail.trip_advance_amount),
        totalExpenses: formatInr(detail.total_expense_amount),
      },
      tripDetails: {
        companyName: dash(company?.name),
        companyLocation: dash(company?.company_location_id?.name),
        miplLocation: dash(td?.mipl_location_id?.name),
        tripRefId: dash(td?.trip_ref_id),
        routeVersionId: dash(td?.route_version_id),
        startDateTime: formatDateTime(td?.start_date_time ?? td?.date_time),
        endDateTime: formatDateTime(td?.trip_end_at),
        idealAverage:
          td?.vehicle_id?.ideal_average != null && !Number.isNaN(Number(td.vehicle_id.ideal_average))
            ? `${Number(td.vehicle_id.ideal_average)} average`
            : "—",
        efficiency: formatFuelEfficiency(fuelForm),
        challan: dash(td?.challan_number),
        vehicle: dash(td?.vehicle_id?.name),
        tripAmountRow: formatInr(td?.trip_amount),
        tripBreakLocation: dash(
          typeof td?.trip_break_location_id === "object" && td.trip_break_location_id != null
            ? td.trip_break_location_id?.name ?? td.trip_break_location_id?._id
            : td?.trip_break_location_id
        ),
      },
      advance,
      debit,
      primaryDriver: {
        name: dash(primary?.driver_name ?? primary?.full_name),
        ref: dash(primary?.driver_ref_id),
        license: dash(
          primary?.license_number ??
            primary?.driver_license_number ??
            primary?.licence_number
        ),
      },
      secondaryDriver: {
        name: dash(secondary?.driver_name ?? secondary?.full_name),
        ref: dash(secondary?.driver_ref_id),
        license: dash(
          secondary?.license_number ??
            secondary?.driver_license_number ??
            secondary?.licence_number
        ),
      },
      hasSecondary: isNonEmptyObject(secondary) && Boolean(secondary?.driver_name),
    };
  }, [detail]);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [viewDetail, setViewDetail] = useState(false);
  const [viewReceipt, setViewReceipt] = useState(false);
  const [showTripBreak, setShowTripBreak] = useState(false);
  const [showDebitNote, setShowDebitNote] = useState(false);

  const [tripBreakLocations, setTripBreakLocations] = useState([]);
  const [tripBreakLoading, setTripBreakLoading] = useState(false);
  const [tripBreakSubmitting, setTripBreakSubmitting] = useState(false);
  const [endTripSubmitting, setEndTripSubmitting] = useState(false);
  const [showEndTripConfirm, setShowEndTripConfirm] = useState(false);
  const [tripBreakLocationId, setTripBreakLocationId] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedImage, setSelectedImage] = useState("");
  const [detailModalText, setDetailModalText] = useState("");

  const prevExpenseTripIdRef = useRef(tripId);

  useEffect(() => {
    let cancelled = false;

    const loadExpenses = async () => {
      if (!tripId) {
        setExpenseRows([]);
        setExpenseTotal(0);
        setExpenseTotalPages(1);
        setExpenseListLoading(false);
        prevExpenseTripIdRef.current = "";
        return;
      }

      const tripChanged = prevExpenseTripIdRef.current !== tripId;
      prevExpenseTripIdRef.current = tripId;
      if (tripChanged && currentPage !== 1) {
        setCurrentPage(1);
        return;
      }

      const token = localStorage.getItem("sessionToken");
      if (!token) {
        setExpenseRows([]);
        setExpenseTotal(0);
        setExpenseTotalPages(1);
        setExpenseListLoading(false);
        return;
      }

      setExpenseListLoading(true);
      const qs = new URLSearchParams({
        trip_id: tripId,
        page: String(currentPage),
        limit: String(itemsPerPage),
      });
      const url = `${ACCOUNTANT_API}${endpoint.listOfExpence}?${qs.toString()}`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await parseBody(response);
        if (cancelled) return;

        if (!response.ok || (payload?.code != null && payload.code !== 200 && payload.code !== 201)) {
          toast.error(getErrorMessage(payload, response));
          setExpenseRows([]);
          setExpenseTotal(0);
          setExpenseTotalPages(1);
          return;
        }

        const d = payload?.data;
        const list = Array.isArray(d?.trip_expence_data) ? d.trip_expence_data : [];
        const total = Number(d?.total);
        const pages = Number(d?.total_pages);

        setExpenseRows(list.map(mapTripExpenceListRow));
        setExpenseTotal(Number.isFinite(total) ? total : list.length);
        setExpenseTotalPages(Number.isFinite(pages) && pages >= 1 ? pages : 1);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error(
            err?.message === "Failed to fetch"
              ? "Network error. Check your connection."
              : "Could not load expense list."
          );
          setExpenseRows([]);
          setExpenseTotal(0);
          setExpenseTotalPages(1);
        }
      } finally {
        if (!cancelled) setExpenseListLoading(false);
      }
    };

    loadExpenses();
    return () => {
      cancelled = true;
    };
  }, [tripId, currentPage, itemsPerPage, location.key]);

  const filteredDrivers = expenseRows.filter(
    (driver) =>
      driver.description.toLowerCase().includes(search.toLowerCase()) ||
      driver.amountSpent.toLowerCase().includes(search.toLowerCase())
  );

  const indexOfFirst = (currentPage - 1) * itemsPerPage;
  const currentDrivers = filteredDrivers;
  const totalPages = expenseTotalPages;

  const closeModal = () => {
    setViewDetail(false);
    setViewReceipt(false);
    setShowTripBreak(false);
    setShowEndTripConfirm(false);
    setShowDebitNote(false);
  };

  useEffect(() => {
    if (!showTripBreak) return undefined;

    let cancelled = false;

    const loadTripBreakLocations = async () => {
      const token = localStorage.getItem("sessionToken");
      if (!token) {
        toast.error("Session expired. Please log in again.");
        setTripBreakLocations([]);
        setTripBreakLoading(false);
        return;
      }

      setTripBreakLoading(true);
      const url = `${ACCOUNTANT_API}${endpoint.listOfAlllocationForTripBreak}`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const payload = await parseBody(response);
        if (cancelled) return;

        if (!response.ok || (payload?.code != null && payload.code !== 200 && payload.code !== 201)) {
          toast.error(getErrorMessage(payload, response));
          setTripBreakLocations([]);
          return;
        }

        const list = pickTripBreakLocationList(payload);
        setTripBreakLocations(list.map(mapTripBreakLocationOption).filter((o) => o.id));
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error("Could not load trip break locations.");
          setTripBreakLocations([]);
        }
      } finally {
        if (!cancelled) setTripBreakLoading(false);
      }
    };

    loadTripBreakLocations();
    return () => {
      cancelled = true;
    };
  }, [showTripBreak]);

  const openTripBreakModal = () => {
    if (
      tripEnded ||
      view?.statusCode === 3 ||
      view?.statusCode === 2 ||
      view?.statusCode === 4 ||
      view?.hasExistingTripBreak
    ) {
      return;
    }
    setTripBreakLocationId("");
    setShowEndTripConfirm(false);
    setShowTripBreak(true);
  };

  const openEndTripConfirmModal = () => {
    if (!tripId.trim()) {
      toast.error("Missing trip.");
      return;
    }
    if (tripEnded) {
      return;
    }
    if (view?.statusCode === 0 || view?.statusCode === 2 || view?.statusCode === 3 || view?.statusCode === 4) {
      return;
    }
    setShowTripBreak(false);
    setShowEndTripConfirm(true);
  };

  const submitTripBreak = async () => {
    if (!tripId.trim()) {
      toast.error("Missing trip.");
      return;
    }
    if (!tripBreakLocationId.trim()) {
      toast.error("Please select a trip break location.");
      return;
    }

    const token = localStorage.getItem("sessionToken");
    if (!token) {
      toast.error("Session expired. Please log in again.");
      return;
    }

    setTripBreakSubmitting(true);
    const url = `${ACCOUNTANT_API}${endpoint.tripBreak}`;
    const body = {
      trip_id: tripId.trim(),
      trip_break_location_id: tripBreakLocationId.trim(),
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const payload = await parseBody(response);

      if (!response.ok) {
        toast.error(getErrorMessage(payload, response));
        return;
      }

      const apiCode = payload?.code;
      if (apiCode != null && apiCode !== 200 && apiCode !== 201) {
        toast.error(getErrorMessage(payload, response));
        return;
      }

      toast.success(payload?.message || "Trip break saved.");
      setShowTripBreak(false);
      setTripBreakLocationId("");
      navigate(viewTripHref, { replace: true });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.message === "Failed to fetch"
          ? "Network error. Check your connection."
          : "Could not save trip break."
      );
    } finally {
      setTripBreakSubmitting(false);
    }
  };

  const submitEndTrip = async () => {
    if (!tripId.trim()) {
      toast.error("Missing trip.");
      return;
    }

    const token = localStorage.getItem("sessionToken");
    if (!token) {
      toast.error("Session expired. Please log in again.");
      return;
    }

    setEndTripSubmitting(true);
    const url = `${ACCOUNTANT_API}${endpoint.endTrip}`;
    const body = { trip_id: tripId.trim() };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const payload = await parseBody(response);

      if (!response.ok) {
        toast.error(getErrorMessage(payload, response));
        return;
      }

      const apiCode = payload?.code;
      if (apiCode != null && apiCode !== 200 && apiCode !== 201) {
        toast.error(getErrorMessage(payload, response));
        return;
      }

      toast.success(payload?.message || "Trip ended successfully.");
      setShowEndTripConfirm(false);
      setTripEnded(true);
      await loadTripDetails({ showLoading: false });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.message === "Failed to fetch"
          ? "Network error. Check your connection."
          : "Could not end trip."
      );
    } finally {
      setEndTripSubmitting(false);
    }
  };

  const Card = ({ children }) => (
    <div className="bg-white rounded-2xl border p-6 mb-4 font-UbuntuMedium">{children}</div>
  );

  const Row = ({ label, value }) => (
    <div className="flex justify-between py-1">
      <span className="text-gray-400">{label}</span>
      <span className="text-gray-800 text-right max-w-[60%] break-words">{value}</span>
    </div>
  );

  const SummaryStatCard = ({ label, value, valueClassName }) => (
    <div className="rounded-xl border border-primary/30 bg-primary/[0.07] px-4 py-5 shadow-sm">
      <div
        className={`text-xl md:text-2xl font-UbuntuMedium tabular-nums leading-tight ${
          valueClassName ?? "text-[#000053]"
        }`}
      >
        {value}
      </div>
      <div className="mt-2 text-sm text-gray-500 font-UbuntuRegular leading-snug">{label}</div>
    </div>
  );

  const renderKeyValueBlock = (obj, labels) => {
    if (!isNonEmptyObject(obj)) {
      return <p className="text-gray-500 text-sm">No details available.</p>;
    }
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        {labels.map(({ key, label, format }) => {
          if (!(key in obj)) return null;
          const val = obj[key];
          let display;
          if (format === "inr") {
            display = formatInr(val);
          } else if (format === "date") {
            if (val == null || val === "") return null;
            display = formatDateTime(val);
          } else if (format === "text") {
            display = dash(val);
          } else {
            if (val == null || val === "") return null;
            display = typeof val === "object" ? JSON.stringify(val) : String(val);
          }
          return (
            <div key={key} className="md:col-span-1">
              <Row label={label} value={display} />
            </div>
          );
        })}
      </div>
    );
  };

  if (!tripId) {
    return (
      <div className="mt-7 rounded-lg px-4 py-6 bg-white mb-4 shadow-sm text-center text-gray-600">
        Missing trip. Open this page from a trip row (view details).
        <div className="mt-3">
          <Link to={viewTripHref} className="text-primary underline">
            Back to trips
          </Link>
        </div>
        <Toastify />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mt-7 rounded-lg px-4 py-8 bg-white mb-4 shadow-sm text-center text-gray-500">
        Loading trip details…
        <Toastify />
      </div>
    );
  }

  if (!view) {
    return (
      <div className="mt-7 rounded-lg px-4 py-6 bg-white mb-4 shadow-sm text-center text-gray-600">
        Could not load trip details.
        <div className="mt-3">
          <Link to={viewTripHref} className="text-primary underline">
            Go back
          </Link>
        </div>
        <Toastify />
      </div>
    );
  }

  const advance = view.advance;
  const debit = view.debit;

  const isTripPending = view.statusCode === 0;
  const isTripCompleted = view.statusCode === 3;
  const isTripLocked = view.statusCode === 2;
  const isTripCancelled = view.statusCode === 4;
  const canAddExpense = !isTripPending && view.tripExpenseButton;
  const tripBreakDisabled =
    tripEnded ||
    tripBreakSubmitting ||
    endTripSubmitting ||
    isTripPending ||
    isTripCompleted ||
    isTripLocked ||
    isTripCancelled ||
    view.hasExistingTripBreak;
  const endTripDisabled =
    tripEnded ||
    tripBreakSubmitting ||
    endTripSubmitting ||
    isTripCompleted ||
    isTripLocked ||
    isTripCancelled ||
    view.statusCode === 0;

  return (
    <>
      <div className="mt-7">
        <div className="flex !mb-5 items-start">
          <div>
            <h3 className="text-xl font-UbuntuMedium !my-0 flex items-center gap-1.5 flex-wrap">
              <span>Status:</span>
              <span className="font-UbuntuBold text-[#000053]">{view.statusLabel}</span>
            </h3>
            {view.tripCancelReason ? (
              <p className="mb-0 mt-1 text-sm font-UbuntuMedium text-red-600">
                {view.tripCancelReason}
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-x-2 ml-auto items-center">
            <Link to={viewTripHref} className="btn btn-primary">
              Back
            </Link>
            {!isTripPending && (
              <>
                {!tripBreakDisabled ? (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={openTripBreakModal}
                  >
                    Trip Break
                  </button>
                ) : null}
                {canAddExpense ? (
                  <Link to={addExpenseHref} className="btn btn-danger">
                    Expense
                  </Link>
                ) : null}
                {!endTripDisabled ? (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={openEndTripConfirmModal}
                  >
                    End Trip
                  </button>
                ) : null}
              </>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          <SummaryStatCard label="Total Trip Amount" value={view.summary.tripAmount} />
          <SummaryStatCard
            label="Award Balance"
            value={view.summary.awardAmount}
            valueClassName={view.summary.isAwardAmountNegative ? "text-danger" : undefined}
          />
          <SummaryStatCard label="Debit Note" value={view.summary.debitNote} />
          <SummaryStatCard label="Trip Advance" value={view.summary.tripAdvance} />
          <SummaryStatCard label="Total Expenses" value={view.summary.totalExpenses} />
        </div>
        <div>
          <div className="bg-white shadow-sm rounded-md p-6 mb-4">
            <h2 className="text-lg font-UbuntuRegular mb-3">Trip Details</h2>
            <Card>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div>
                  <Row label="Company Name" value={view.tripDetails.companyName} />
                  <Row label="Company Location" value={view.tripDetails.companyLocation} />
                  <Row label="MIPL Location" value={view.tripDetails.miplLocation} />
                </div>
                <div>
                  <Row label="Trip ID" value={view.tripDetails.tripRefId} />
                  {/* <Row label="Route Version ID" value={view.tripDetails.routeVersionId} /> */}
                  <Row label="Start Date & Time" value={view.tripDetails.startDateTime} />
                  <Row label="End Date & Time" value={view.tripDetails.endDateTime} />
                </div>
              </div>
            </Card>
            <Card>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div>
                  <Row label="Ideal Average" value={view.tripDetails.idealAverage} />
                  <Row label="Efficiency" value={view.tripDetails.efficiency} />
                  <Row label="Trip Amount" value={view.tripDetails.tripAmountRow} />
                </div>
                <div>
                  <Row label="Delivery Challan Number" value={view.tripDetails.challan} />
                  <Row label="Vehicle" value={view.tripDetails.vehicle} />
                  <Row label="Trip Break Location" value={view.tripDetails.tripBreakLocation} />
                </div>
              </div>
            </Card>
          </div>
          <h2 className="text-lg font-UbuntuRegular mt-6 mb-3">Trip Advance Details</h2>
          <Card>
            {isNonEmptyObject(advance) ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div>
                  <Row label="Driver Name" value={dash(advance.driver_name)} />
                  <Row label="Driver ID" value={dash(advance.driver_ref_id)} />
                  <Row label="Advance Amount" value={formatInr(advance.advance_amount)} />
                </div>
                <div>
                  <Row label="Fingerprint Signature" value={dash(advance.fingerprint_signature)} />
                  <Row label="Created At" value={formatDateTime(advance.createdAt)} />
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No advance details.</p>
            )}
          </Card>

          <h2 className="text-lg font-UbuntuRegular mt-6 mb-3">Debit Note Payment Details</h2>
          <Card>
            {isNonEmptyObject(debit) ? (
              <>
                {renderKeyValueBlock(debit, [
                  { key: "amount_provided", label: "Amount Provided", format: "inr" },
                  { key: "amount", label: "Amount", format: "inr" },
                  { key: "debit_note_amount", label: "Debit Note Amount", format: "inr" },
                  { key: "createdAt", label: "Created At", format: "date" },
                  { key: "updatedAt", label: "Updated At", format: "date" },
                  { key: "date_time", label: "Date & Time", format: "date" },
                  { key: "verification_type", label: "Verification Type", format: "text" },
                ])}
                {debit.description != null && debit.description !== "" && (
                  <div className="mt-4">
                    <div className="text-gray-400">Description</div>
                    <p className="text-gray-800 mt-1">{String(debit.description)}</p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-gray-500 text-sm">No debit note details.</p>
            )}
          </Card>
          <h2 className="text-lg font-UbuntuRegular mt-6 mb-3">Driver Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <Card>
              <div className="text-lg font-UbuntuMedium mb-3 -mt-2">Primary Driver</div>
              <div>
                <Row label="Driver Name" value={view.primaryDriver.name} />
                <Row label="Driver Ref ID" value={view.primaryDriver.ref} />
                <Row label="License Number" value={view.primaryDriver.license} />
              </div>
            </Card>
            <Card>
              <div className="text-lg font-UbuntuMedium mb-3 -mt-2">Secondary Driver</div>
              <div>
                {view.hasSecondary ? (
                  <>
                    <Row label="Driver Name" value={view.secondaryDriver.name} />
                    <Row label="Driver Ref ID" value={view.secondaryDriver.ref} />
                    <Row label="License Number" value={view.secondaryDriver.license} />
                  </>
                ) : (
                  <p className="text-gray-500 text-sm">No secondary driver.</p>
                )}
              </div>
            </Card>
          </div>
        </div>

        <h3 className="text-xl font-UbuntuMedium !mb-2.5">All Expenses Payment Transition Details</h3>
        <div className="common-table-wrapper m-0 shadow-sm">
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Amount Spent</th>
                  <th>Description</th>
                  <th>Date & Time</th>
                  <th>Receipt Image</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {expenseListLoading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-gray-500">
                      Loading expenses…
                    </td>
                  </tr>
                ) : currentDrivers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-gray-500">
                      No expense records for this trip.
                    </td>
                  </tr>
                ) : (
                  currentDrivers.map((driver, index) => (
                    <tr key={driver.id}>
                      <td>{indexOfFirst + index + 1}</td>
                      <td>{driver.amountSpent}</td>
                      <td>{driver.description}</td>
                      <td>{driver.dateTime}</td>
                      <td>
                        {driver.receipt ? (
                          <img
                            src={driver.receipt}
                            className="max-w-24 border max-h-10 mx-auto cursor-pointer"
                            alt=""
                            onClick={() => {
                              setSelectedImage(driver.receipt);
                              setViewReceipt(true);
                            }}
                          />
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <div className="flex items-center justify-center gap-2">
                          <img
                            src={EyeIcon}
                            className="h-7 cursor-pointer"
                            alt=""
                            onClick={() => {
                              setDetailModalText(driver.description);
                              setViewDetail(true);
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex gap-x-4 items-center mt-4">
          <select
            className="p-2 shadow-sm outline-none"
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>
          <div className="text-sm text-gray-500 ml-auto">{expenseTotal} records found</div>
          <div className="flex items-center gap-2 pr-8">
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || expenseListLoading || expenseTotal === 0}
              className={`!bg-primary text-white w-[35px] h-[35px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${
                currentPage === 1 ? "" : "cursor-pointer"
              }`}
            >
              <img src={LeftAngle} className="invert grayscale brightness-100" alt="" />
            </button>
            <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="h-[35px] w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
            <span className="text-sm text-gray-600">of {totalPages}</span>
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || expenseListLoading || expenseTotal === 0}
              className={`!bg-primary text-white w-[35px] h-[35px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${
                currentPage === totalPages ? "" : "cursor-pointer"
              }`}
            >
              <img src={RightAngle} className="invert grayscale brightness-100" alt="" />
            </button>
          </div>
        </div>
      </div>

      {viewDetail && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999]">
          <div className="bg-white py-6 px-8 rounded-xl max-w-screen-md text-center font-UbuntuRegular">
            <p className="mb-4 text-xl font-UbuntuMedium">View Expense Description</p>
            <p className="text-left">{detailModalText || "—"}</p>
            <div className="flex justify-center gap-4 mt-6 mb-2">
              <button type="button" className="btn btn-danger !px-10" onClick={closeModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {viewReceipt && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999]">
          <div className="bg-white py-6 px-8 rounded-xl max-w-screen-md text-center font-UbuntuRegular">
            <p className="mb-4 text-xl font-UbuntuMedium">Receipt Image</p>
            <img src={selectedImage} className="max-h-[400px] mx-auto" alt="" />
            <div className="flex justify-center gap-4 mt-6 mb-2">
              <button type="button" className="btn btn-danger !px-10" onClick={closeModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showTripBreak && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999] p-4">
          <div className="bg-white p-6 rounded-xl w-full max-w-lg text-left shadow-lg">
            <h2 className="text-lg font-UbuntuMedium mb-1 text-center">Trip Break</h2>
            <p className="text-xs text-gray-500 mb-4 text-center">
              Select a trip break location ({tripBreakLocations.length} available)
            </p>
            <label className="block text-sm font-UbuntuMedium mb-1" htmlFor="trip-break-location">
              Location
            </label>
            <select
              id="trip-break-location"
              className="w-full border rounded-md p-2 mb-6 outline-none form-control"
              value={tripBreakLocationId}
              onChange={(e) => setTripBreakLocationId(e.target.value)}
              disabled={tripBreakLoading || tripBreakSubmitting}
            >
              <option value="">
                {tripBreakLoading ? "Loading locations…" : "Select location"}
              </option>
              {tripBreakLocations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.label}
                </option>
              ))}
            </select>
            <div className="flex justify-center gap-2">
              <button
                type="button"
                className="btn btn-primary"
                disabled={tripBreakSubmitting}
                onClick={() => {
                  setShowTripBreak(false);
                  setTripBreakLocationId("");
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={
                  !tripBreakLocationId.trim() || tripBreakLoading || tripBreakSubmitting
                }
                onClick={submitTripBreak}
              >
                {tripBreakSubmitting ? "Submitting…" : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showEndTripConfirm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999] p-4">
          <div className="bg-white p-6 rounded-xl w-full max-w-lg text-left shadow-lg">
            <h2 className="text-lg font-UbuntuMedium mb-2 text-center">End Trip</h2>
            <p className="text-sm text-gray-600 mb-6 text-center">
            Are you sure you want to end this trip? This will mark the trip as completed.
            </p>
            <div className="flex justify-center gap-2">
              <button
                type="button"
                className="btn btn-primary"
                disabled={endTripSubmitting}
                onClick={() => setShowEndTripConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={endTripSubmitting}
                onClick={submitEndTrip}
              >
                {endTripSubmitting ? "Ending…" : "End Trip"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showDebitNote && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999]">
          <div className="bg-white p-6 rounded-xl w-[450px]">
            <h2 className="text-lg font-UbuntuMedium mb-4 text-center">Debit Note</h2>
            <div className="mb-4">
              <label className="block text-sm mb-1">Enter Amount</label>
              <input type="text" placeholder="₹ 1500" className="w-full border rounded-md p-2 outline-none" />
            </div>
            <div className="mb-6">
              <label className="block text-sm mb-1">Description</label>
              <textarea rows="4" className="w-full border rounded-md p-2 outline-none" />
            </div>
            <div className="flex justify-center gap-4">
              <button type="button" className="btn btn-primary" onClick={() => setShowDebitNote(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger !px-12" onClick={() => setShowDebitNote(false)}>
                Next
              </button>
            </div>
          </div>
        </div>
      )}
      <Toastify />
    </>
  );
};

export default ViewTripDetail;
